import { FindResult, Level, LevelDefaults } from './level';
import { Word } from './word';

export const findOne = (lvl: Level, x: number, y: number): FindResult | null => {
  const result = findAll(lvl, x, y);
  return result.length === 0 ? null : result[0];
};

export const findAll = (lvl: Level, x: number, y: number): FindResult[] => {
  const result = [];
  for (let i = 0; i < lvl.words.length; i++) {
    const obj = lvl.words[i];
    const ax = obj.metadata.isVertical ? 0 : 1;
    const ay = obj.metadata.isVertical ? 1 : 0;

    for (let l = 0; l < getSize(obj); l++) {
      if (obj.metadata.x + ax * l === x && obj.metadata.y + ay * l === y) {
        result.push({ obj: obj, ax: ax * l, ay: ay * l });
        break;
      }
    }
  }

  return result;
};

export const LevelWidth = (lvl: Level): number => {
  let max = 0;
  for (let i = 0; i < lvl.words.length; i++) {
    const val =
      lvl.words[i].metadata.x +
      (lvl.words[i].metadata.isVertical ? 0 : 1) * lvl.words[i].word.length;
    if (val > max) {
      max = val;
    }
  }

  return max > 0 ? max : 1;
};

export const LevelHeight = (lvl: Level): number => {
  let max = 0;
  for (let i = 0; i < lvl.words.length; i++) {
    const val =
      lvl.words[i].metadata.y +
      (lvl.words[i].metadata.isVertical ? 1 : 0) * lvl.words[i].word.length;
    if (val > max) {
      max = val;
    }
  }

  return max > 0 ? max : 1;
};

export const validateAll = (lvl: Level): string[] => {
  return lvl.words.filter((word) => !validate(lvl, word)).map((word) => word.word);
};

export const validate = (lvl: Level, word: Word): boolean => {
  let result = _validateByLocation(lvl, word);
  result = result ? _validateByEdges(lvl, word) : false;
  result = result ? _validateBySides(lvl, word) : false;
  result = result ? _validateByCross(lvl, word) : false;
  return result;
};

export const getKeyword = (level: Level): string => {
  const words = level.words
    .filter((w) => w.metadata.isLocked)
    .map((w) => w.word)
    .sort((a, b) => b.length - a.length)
    .slice(0, 1);

  return words.length !== 0 ? words[0] : '';
};

const _validateByLocation = (lvl: Level, word: Word): boolean => {
  if (word.metadata.x < 0 || word.metadata.y < 0) {
    return false;
  }

  const width = lvl.width || LevelDefaults.DefaultWidth;
  const height = lvl.height || LevelDefaults.DefaultHeight;
  const size = getSize(word);
  if (!word.metadata.isVertical && word.metadata.x + size > width) {
    return false;
  }

  if (word.metadata.isVertical && word.metadata.y + size > height) {
    return false;
  }

  return true;
};

const _validateByEdges = (lvl: Level, word: Word): boolean => {
  const ax = word.metadata.isVertical ? 0 : 1;
  const ay = word.metadata.isVertical ? 1 : 0;

  const before = findOne(lvl, word.metadata.x + ax * -1, word.metadata.y + ay * -1);
  if (before) {
    return false;
  }

  const size = getSize(word);
  const after = findOne(lvl, word.metadata.x + ax * size, word.metadata.y + ay * size);
  return after === null;
};

const _validateBySides = (lvl: Level, word: Word): boolean => {
  let others = [];
  if (word.metadata.isVertical) {
    others = lvl.words.filter(
      (x) =>
        x !== word &&
        x.metadata.isVertical === word.metadata.isVertical &&
        Math.abs(x.metadata.x - word.metadata.x) <= 1 &&
        x.metadata.y <= lastY(word) &&
        lastY(x) >= word.metadata.y
    );
  } else {
    others = lvl.words.filter(
      (x) =>
        x !== word &&
        x.metadata.isVertical === word.metadata.isVertical &&
        Math.abs(x.metadata.y - word.metadata.y) <= 1 &&
        x.metadata.x <= lastX(word) &&
        lastX(x) >= word.metadata.x
    );
  }

  return others.length === 0;
};

const _validateByCross = (lvl: Level, word: Word): boolean => {
  let result = true;
  const size = getSize(word);
  const ax = word.metadata.isVertical ? 0 : 1;
  const ay = word.metadata.isVertical ? 1 : 0;

  for (let i = 0; i < size; i++) {
    const x = word.metadata.x + ax * i;
    const y = word.metadata.y + ay * i;

    const found = findAll(lvl, x, y).map((x) => x.obj);
    if (found.length === 1) {
      continue;
    } else if (found.length === 2) {
      if (found[0].metadata.isVertical === found[1].metadata.isVertical) {
        result = false;
        continue;
      } else if (
        getValue(found[0], x, y) !== null &&
        getValue(found[0], x, y) !== getValue(found[1], x, y)
      ) {
        result = false;
        continue;
      }
    } else {
      result = false;
      continue;
    }
  }

  return result;
};

export const getSize = (word: Word): number => {
  return word.word.length;
};

export const lastX = (word: Word): number => {
  return !word.metadata.isVertical ? word.metadata.x + getSize(word) - 1 : word.metadata.x;
};

export const lastY = (word: Word): number => {
  return word.metadata.isVertical ? word.metadata.y + getSize(word) - 1 : word.metadata.y;
};

export const getValue = (word: Word, x: number, y: number): string | null => {
  const ax = word.metadata.isVertical ? 0 : 1;
  const ay = word.metadata.isVertical ? 1 : 0;
  for (let l = 0; l < getSize(word); l++) {
    if (word.metadata.x + ax * l === x && word.metadata.y + ay * l === y) {
      return word.word[l];
    }
  }

  return null;
};

export const getHash = (lvl: Level) => {
  const level = `${lvl.id}${lvl.lang}${lvl.keyword}.`;
  const words = lvl.words
    .map(
      (w) => `${w.word}${w.metadata.x}${w.metadata.y}${w.metadata.isVertical}${w.metadata.isLocked}`
    )
    .sort()
    .join('.');
  return level + words;
};
